/**
 * ManagementHeader Component
 *
 * Header for Platform Management Console
 * Displays user info, logout, and mobile menu toggle
 */

import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';

interface ManagementHeaderProps {
  onMenuToggle?: () => void;
}

// Every role that reaches this console. A role missing here shows the wrong badge, which
// misrepresents the scoped roles and misleads role testing.
const ROLE_BADGES: Record<string, { label: string; short: string }> = {
  superadmin: { label: 'Superadmin', short: 'SA' },
  enterprise_admin: { label: 'Enterprise Admin', short: 'EA' },
  channel_partner_admin: { label: 'Partner Admin', short: 'PA' },
  partner_admin: { label: 'Partner Admin', short: 'PA' },
};

const ManagementHeader: React.FC<ManagementHeaderProps> = ({ onMenuToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Unknown roles fall back to their raw role rather than to "Superadmin": naming the role wrong
  // is worse than naming it awkwardly.
  const badge = ROLE_BADGES[user?.role ?? ''] ?? { label: user?.role ?? 'Unknown', short: '—' };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-sm border-b border-purple-100 shadow-sm">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Left: Mobile menu button and page indicator */}
          <div className="flex items-center space-x-3">
            {/* Mobile menu button */}
            <button
              onClick={onMenuToggle}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-purple-700 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-11 min-w-11"
              aria-label="Open menu"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <div className="flex items-center space-x-2 text-purple-700">
              <ShieldCheckIcon className="h-5 w-5" />
              <span className="font-semibold hidden sm:inline">Platform Administration</span>
              <span className="font-semibold sm:hidden">Admin</span>
            </div>
          </div>

          {/* Right: User menu */}
          <div className="flex items-center gap-x-4">
            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center gap-x-2 sm:gap-x-3 rounded-full bg-purple-50 px-3 sm:px-4 py-2 min-h-11 text-sm font-semibold text-purple-900 hover:bg-purple-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2">
                <UserCircleIcon className="h-5 w-5" />
                <span className="hidden sm:inline">{user?.firstName} {user?.lastName}</span>
                <span className="inline-flex items-center rounded-full bg-purple-200 px-2 py-0.5 text-xs font-medium text-purple-800">
                  <span className="hidden sm:inline">{badge.label}</span>
                  <span className="sm:hidden">{badge.short}</span>
                </span>
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                    </div>

                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                        >
                          <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" />
                          Logout
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default ManagementHeader;
